import { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";

const CLAMP_LINES = 5;

interface TestimonialCardProps {
  name: string;
  role: string;
  quote: string;
  hasBorder?: boolean;
}

const TestimonialCard = ({ name, role, quote }: TestimonialCardProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [expanded, setExpanded] = useState(false);

  const lineHeightPx = 22;
  const approxCharsPerLine = 42;
  const estimatedLines = Math.ceil(quote.length / approxCharsPerLine);
  const needsClamp = estimatedLines > CLAMP_LINES;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Card
      sx={{
        height: expanded ? "auto" : "401px",
        minHeight: "401px",
        bgcolor: "#f4ede8",
        borderRadius: "33px",
        p: "42px 41px",
        position: "relative",
        boxShadow: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Decorative quote mark */}
      <Typography
        sx={{
          fontFamily: "Mulish",
          fontWeight: 700,
          fontSize: "200px",
          color: "#434343",
          opacity: 0.1,
          lineHeight: "0.1px",
          position: "absolute",
          top: "42px",
          left: 0,
          width: "135px",
          height: "73px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        &quot;
      </Typography>

      <CardContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        {/* Quote text */}
        <Box sx={{ mt: "96px", flex: 1 }}>
          <Typography
            sx={{
              fontFamily: "Mulish",
              fontWeight: 400,
              fontSize: { xs: "14px", md: "15px" },
              color: "#434343",
              lineHeight: `${lineHeightPx}px`,
              ...(!expanded && needsClamp
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: CLAMP_LINES,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {quote}
          </Typography>

          {needsClamp && (
            <Typography
              onClick={() => setExpanded((p) => !p)}
              sx={{
                fontFamily: "Mulish",
                fontWeight: 700,
                fontSize: "13px",
                color: "#B07650",
                cursor: "pointer",
                mt: "6px",
                "&:hover": { opacity: 0.75 },
              }}
            >
              {expanded ? "Read less" : "Read more"}
            </Typography>
          )}
        </Box>

        {/* Author chip */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            bgcolor: colors.primary[900],
            borderRadius: "26.74px",
            p: "6.3px",

            mt: "20px",
          }}
        >
          <Avatar
            sx={{
              width: "43.68px",
              height: "43.68px",
              bgcolor: "#B07650",
              color: "#fff",
              fontWeight: 700,
              fontSize: "14px",
            }}
          >
            {getInitials(name)}
          </Avatar>
          <Stack spacing={0} justifyContent="center">
            <Typography
              sx={{
                fontFamily: "Mulish",
                fontWeight: 700,
                fontSize: "14px",
                color: colors.primary[700],
                lineHeight: "24px",
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Mulish",
                fontWeight: 700,
                fontSize: "12px",
                color: "#434343",
                lineHeight: "24px",
              }}
            >
              {role}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TestimonialCard;
